import React, { useCallback } from "react";
import { useRatingWorkflow } from "../../../../context/RatingWorkflowContext";
import { CategoryRatingSection } from "./CategoryRatingSection";
import type {
	KpiCategory,
	SubCriterionRating,
} from "newui/features/MySuppliers/types/supplier-performance.types";

interface RateCategoriesStepProps {
	categories: KpiCategory[];
	previousRatings?: Record<number, SubCriterionRating[]>;
}

export const RateCategoriesStep: React.FC<RateCategoriesStepProps> = ({
	categories,
	previousRatings = {},
}) => {
	const { state, dispatch } = useRatingWorkflow();

	const selectedCategories = categories.filter((cat) =>
		state.selectedCategoryIds.includes(cat.id)
	);

	const handleScoreChange = useCallback(
		(categoryId: number, subCriterionId: number, score: number) => {
			const existing = state.ratings[categoryId]?.find(
				(r) => r.subCriterionId === subCriterionId
			);
			dispatch({
				type: "SET_SUB_CRITERION_RATING",
				payload: {
					categoryId,
					subCriterionId,
					score,
					comment: existing?.comment ?? null,
				},
			});
		},
		[dispatch, state.ratings]
	);

	const handleCommentChange = useCallback(
		(categoryId: number, subCriterionId: number, comment: string) => {
			const existing = state.ratings[categoryId]?.find(
				(r) => r.subCriterionId === subCriterionId
			);
			dispatch({
				type: "SET_SUB_CRITERION_RATING",
				payload: {
					categoryId,
					subCriterionId,
					score: existing?.score ?? 0,
					comment: comment || null,
				},
			});
		},
		[dispatch, state.ratings]
	);

	return (
		<div data-testid="rate-categories-step">
			<h3 className="mb-2 text-lg font-semibold text-gray-900">
				Rate Performance
			</h3>
			<p className="mb-6 text-sm text-gray-600">
				Rate each sub-criterion using the dial (1-9). All scores are
				required to proceed.
			</p>

			{selectedCategories.map((category) => (
				<CategoryRatingSection
					key={category.id}
					category={category}
					ratings={state.ratings[category.id] ?? []}
					previousRatings={previousRatings[category.id]}
					onScoreChange={handleScoreChange}
					onCommentChange={handleCommentChange}
				/>
			))}
		</div>
	);
};
